import { IDKeyTypes } from 'app/model/id-manager';
import { ObjID } from './../../model/id-manager';
import { SimpleHandler } from './../../types/handlers';
import { AttributeId, ConfigAttribute, AttributeOptionId, AvailCriteria } from './../../types/project-config';
import { ComparisonKeys, Comparisons, Criterion } from './../../model/comparisons';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Project } from 'app/model/project';

@Component({
  selector: 'app-attribute-comparison',
  templateUrl: './attribute-comparison.component.html',
  styleUrls: ['./attribute-comparison.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AttributeComparisonComponent implements OnInit {

  @Input()
  private _criteria?: AvailCriteria | undefined;
  private _ready = false;

  @Input()
  public set criteria(criteria: AvailCriteria | undefined) {
    if (!(this.project && this.attribute)) {
      this.Defer(() => {
        this.criteria = criteria;
      })
      return;
    }
    this._criteria = criteria;
    if (!!this._criteria) {
      this.selectedAttributeId = this._criteria.attribute_id;
      this.comparison = this._criteria.comparison;
      if (!!this._criteria.value) {
        this.value = this._criteria.value;
      } else {
        this.value = '';
      }
      if (!!this._criteria.option_id) {
        this.optionId = this._criteria.option_id;
      } else {
        this.optionId = 0;
      }
    }
    window.requestAnimationFrame(() => {
      this.cdr.markForCheck();
      this.Refresh();
    })
  }
  public get criteria(): AvailCriteria | undefined {
    if (!this._criteria) return undefined;
    return this._criteria;
  }

  public attributeName = '';

  private _attributeId?: AttributeId;
  @Input()
  public set attribute(attributeId: AttributeId | undefined) {
    if (!this.project) {
      this.Defer(() => {
        this.attribute = attributeId;
      });
      return;
    }
    this._attributeId = attributeId;
    if (attributeId === undefined) return;
    const attInfo = this.project.GetAttributeById(attributeId);
    if (attInfo !== false) {
      this.attributeName = attInfo.label;
    }
    this.attributeInfo = (attInfo === false) ? undefined : attInfo;
    this.cdr.markForCheck();
    this.Refresh();
  }
  public get attribute(): AttributeId | undefined {
    return this._attributeId;
  }
  private attributeInfo?: ConfigAttribute;

  @Input()
  public value: string | boolean | AttributeOptionId = '';


  @Output()
  changes = new EventEmitter<void>();


  public attributeId?: AttributeId;

  private _project: Project | undefined;

  @Input()
  public set project(project: Project | undefined) {
    this._project = project;
    this.cdr.markForCheck();
    this.Refresh();
  }
  public get project(): Project | undefined {
    return this._project;
  }
  get isSelector(): boolean {
    if (!this.selectedAttribute) return false;
    return this.selectedAttribute.input === 'selector';
  }

  get isInput(): boolean {
    if (!this.selectedAttribute) return false;
    return this.selectedAttribute.input === 'input';
  }
  get isCheckbox(): boolean {
    if (!this.selectedAttribute) return false;
    return this.selectedAttribute.input === 'checkbox';
  }
  get requiresValue(): boolean {
    if (this.selectedAttribute === undefined) return false;
    if (['checkbox', 'input'].indexOf(this.selectedAttribute.input) === -1) return false;
    if (this.selectedAttribute.input === 'checkbox') return false;
    if (!this.comparison) return false;
    const comparison = Comparisons[this.comparison];
    if (!comparison) return false;
    return comparison.requiresValue;

  }

  private _comparison: ComparisonKeys = ComparisonKeys.None;
  public get comparison(): ComparisonKeys {
    if (this.isSelector === true) return ComparisonKeys.Equals;
    return this._comparison;
  }
  public set comparison(compare: ComparisonKeys) {
    this._comparison = compare;
    this.Refresh();
  }

  private _selectedAttributeId?: ObjID;
  public set selectedAttributeId(attrId: ObjID | undefined) {
    if (attrId === undefined) {
      if (!this._selectedAttribute !== undefined) {
        this._selectedAttribute = undefined;
        this.cdr.markForCheck();
        this.Refresh();
      }
      return;

    }
    if (!this.project) {
      this.Defer(() => {
        this.selectedAttributeId = attrId;
      });
      return;
    }
    this._selectedAttributeId = attrId;
    this.cdr.markForCheck();
    this.Refresh();
  }
  public get selectedAttributeId(): AttributeId | undefined {
    return this._selectedAttributeId;
  }

  private _selectedAttribute?: ConfigAttribute;
  public set selectedAttribute(attr: ConfigAttribute | undefined) {
    this._selectedAttribute = attr;
    this.cdr.markForCheck();
    this.Refresh();
  }
  public get selectedAttribute(): ConfigAttribute | undefined {
    if (!this._selectedAttributeId) return undefined;
    if (this._selectedAttribute === undefined) {
      this.SetAttributeById();
    } else if (this._selectedAttribute.id !== this._selectedAttributeId) {
      this.SetAttributeById();
    }
    return this._selectedAttribute;
  }
  private SetAttributeById(): void {
    if (!this.project) return;
    if (!this._selectedAttributeId) return;
    this._selectedAttribute = undefined;
    const attr = this.project.GetAttributeById(this._selectedAttributeId);
    if (!!attr) {
      this._selectedAttribute = attr;
    }
    this.cdr.markForCheck();
    this.Refresh();
  }

  private _optionId?: ObjID;
  public set optionId(id: ObjID) {
    if (!(this.project && this.attribute)) {
      this.Defer(() => {
        this.optionId = id;
      });
      return;
    }

    this._optionId = id;
    this.Refresh();
  }
  public get optionId(): ObjID {
    if (!!this._optionId) {
      return this._optionId;
    }
    if (!this.selectedAttribute) return 0;
    if (this.selectedAttribute.hasOwnProperty('default')) {
      if (typeof this.selectedAttribute.default !== 'number') return 0;
      return this.selectedAttribute.default as AttributeOptionId;
    }
    return 0;
  }

  constructor(private cdr: ChangeDetectorRef) { }


  ngOnInit(): void {

  }
  Refresh() {
    if (this._ready === true) {
      this.cdr.detectChanges();
    }
  }
  Defer(handler: SimpleHandler) {
    window.requestAnimationFrame(() => {
      handler();
    })
  }

  NotifyListeners() {
    window.requestAnimationFrame(() => {
      this.cdr.detectChanges();
      this.changes.emit();;
    });
  }
  ToCriteria(): AvailCriteria | undefined {
    if (!this.project) return undefined;
    if (!this.attribute) return undefined;
    if (!this.selectedAttributeId) return undefined;
    if (!this.comparison) return undefined;
    const featureType = this.project.GetFeatureType(this.attribute);
    if (featureType === false) return undefined;
    let id: ObjID = 0;
    switch (featureType) {
      case 'point':
        id = this.project.idManager.ID(IDKeyTypes.PointAttributeCondition);
        break;
      case 'segment':
        id = this.project.idManager.ID(IDKeyTypes.SegmentAttributeCondition);
        break;
      case 'suppl':
        return undefined;
    }
    // if we started with a criteria object, update it.
    if (!!this._criteria) {
      this._criteria.attribute_id = this.selectedAttributeId;
      this._criteria.comparison = this.comparison;
      this._criteria.type = featureType;
      if (Comparisons[this.comparison].requiresValue === true) {
        this._criteria.value = this.value;
      }
      if (!!this.optionId) {
        if (this.optionId > 0) {
          this._criteria.option_id = this.optionId;
        }
      }
      return this._criteria;
    }
    // otherwise return a new criteria object with a new registered id.
    const criteria: AvailCriteria = {
      id: (id),
      attribute_id: this.selectedAttributeId,
      comparison: this.comparison,
      type: featureType
    }
    if (Comparisons[this.comparison].requiresValue === true) {
      criteria.value = this.value;
    }
    if (!!this.optionId) {
      criteria.option_id = this.optionId;
    }
    this.project.idRegistry.RegisterID(criteria.id, criteria);
    return criteria;
  }
}
